import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from 'angular-web-storage';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';
import { environment } from 'src/environment/environment';
import {
  MailboxComposeMode,
  MailboxConversation,
  MailboxDeleteResult,
  MailboxDraft,
  MailboxError,
  MailboxErrorKind,
  MailboxFolder,
  MailboxManagedFolder,
  MailboxMessage,
  MailboxMessageList,
  MailboxSearch,
  MailboxSendRequest,
  MailboxSendResult
} from '../_types/mailbox';

/// Cliente de los endpoints del buzón.
///
/// **No extiende `Service<R>`** del cliente JSON:API propio, y es deliberado por dos motivos independientes:
///
/// 1. Esos endpoints no son JSON:API: no hay recurso persistido detrás, el buzón vive en el servidor de correo.
/// 2. Y más importante: `Service.all()` y `Service.get()` hacen `subscribe(next)` sobre un `AsyncSubject` **sin
///    callback de error**, así que cuando la petición falla el subject no emite ni completa nunca y un
///    `await firstValueFrom(...)` se queda colgado para siempre. Un proxy IMAP falla a menudo (tiempos de
///    espera, credenciales, servidor caído), de modo que usar esa clase base sería un generador de pantallas
///    congeladas.
///
/// Construye su propio `HttpClient` sobre `HttpBackend`, igual que `AuditService`, para saltarse los tres
/// interceptores de la aplicación. Eso evita tres cosas: un aviso flotante por cada fallo de IMAP, que el
/// contador del cargador global suba en cada mensaje que se abre, y sobre todo que un 401 cierre la sesión del
/// usuario. El backend nunca devuelve 401 por fallos del servidor de correo, pero saltarse el interceptor es la
/// segunda barrera.
@Injectable({ providedIn: 'root' })
export class MailboxService {
  private readonly http: HttpClient;
  private readonly storage = inject(LocalStorageService);

  /// Plazo de las operaciones normales.
  private static readonly TIMEOUT_MS = 25000;

  /// Plazo de las búsquedas dentro del cuerpo, que son mucho más lentas en el servidor.
  private static readonly SEARCH_TIMEOUT_MS = 50000;

  /// Plazo del envío: conectar por SMTP, subir los adjuntos y guardar la copia en Enviados lleva su tiempo.
  private static readonly SEND_TIMEOUT_MS = 120000;

  constructor() {
    this.http = new HttpClient(inject(HttpBackend));
  }

  private baseUrl(mailAccountId: string): string {
    return `${environment.api_gw}communications/mailbox/${mailAccountId}`;
  }

  /// Al saltarse los interceptores hay que poner el token a mano.
  private options(params?: HttpParams) {
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.storage.get('token')}`,
        //el buzón se lee en vivo: ninguna respuesta puede venir de la caché del navegador.
        //
        //El servidor ya manda `no-store`, pero eso no sirve para lo que YA está guardado, y esto no es teórico:
        //un 410 «el mensaje ya no está en esta carpeta» es cacheable según el RFC, y un error de una versión
        //anterior se quedó guardado en disco. El navegador lo seguía sirviendo él mismo en cada intento, con lo
        //que el mensaje parecía roto para siempre —sobrevivía a reiniciar la API y a recargar forzando— y en el
        //registro de la API no aparecía ni la petición.
        //
        //Pidiéndolo así se obliga a preguntar al servidor, y de paso se descartan las respuestas envenenadas que
        //cualquier usuario tenga ya en su navegador.
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }),
      params
    };
  }

  // --------------------------------------------------------------- lectura

  /// Árbol del buzón: solo las carpetas suscritas, más la entrada. Para cambiar qué se ve, `getManagedFolders`
  /// y `setFolderSubscription`.
  getFolders(mailAccountId: string, withCounts: boolean = true): Observable<MailboxFolder[]> {
    const params = new HttpParams().set('withCounts', withCounts);
    return this.http
      .get<MailboxFolder[]>(`${this.baseUrl(mailAccountId)}/folders`, this.options(params))
      .pipe(timeout(MailboxService.TIMEOUT_MS), catchError(this.toMailboxError));
  }

  /// Todas las carpetas con su estado de suscripción, para el diálogo de administración.
  getManagedFolders(mailAccountId: string): Observable<MailboxManagedFolder[]> {
    return this.http
      .get<MailboxManagedFolder[]>(`${this.baseUrl(mailAccountId)}/folders/manage`, this.options())
      .pipe(timeout(MailboxService.TIMEOUT_MS), catchError(this.toMailboxError));
  }

  /// Suscribe o desuscribe una carpeta. La suscripción vive en el servidor de correo, así que el cambio se nota
  /// también desde cualquier otro cliente que use la cuenta.
  setFolderSubscription(mailAccountId: string, folder: string, subscribed: boolean): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl(mailAccountId)}/folders/subscription`, { folder, subscribed }, this.options())
      .pipe(timeout(MailboxService.TIMEOUT_MS), catchError(this.toMailboxError));
  }

  listMessages(
    mailAccountId: string,
    folder: string,
    page: number,
    pageSize: number,
    search?: MailboxSearch
  ): Observable<MailboxMessageList> {
    let params = new HttpParams()
      .set('folder', folder)
      .set('page', page)
      .set('pageSize', pageSize);

    if (search?.text) params = params.set('text', search.text);
    if (search?.from) params = params.set('from', search.from);
    if (search?.subject) params = params.set('subject', search.subject);
    if (search?.unseenOnly) params = params.set('unseenOnly', true);
    if (search?.flaggedOnly) params = params.set('flaggedOnly', true);
    if (search?.searchBody) params = params.set('searchBody', true);

    const limit = search?.searchBody ? MailboxService.SEARCH_TIMEOUT_MS : MailboxService.TIMEOUT_MS;

    return this.http
      .get<MailboxMessageList>(`${this.baseUrl(mailAccountId)}/messages`, this.options(params))
      .pipe(
        timeout(limit),
        map(result => ({
          ...result,
          //el interceptor que convierte fechas no se aplica aquí, así que se convierten a mano
          items: (result.items ?? []).map(item => ({ ...item, date: new Date(item.date) }))
        })),
        catchError(this.toMailboxError)
      );
  }

  /// Los mensajes de la conversación de un mensaje, dentro de la misma carpeta.
  ///
  /// Se pide solo al desplegar una conversación en el listado: agrupar lo que ya está en pantalla no cuesta
  /// petición alguna, porque cada mensaje trae su clave.
  getConversation(
    mailAccountId: string,
    folder: string,
    uid: number,
    uidValidity: number
  ): Observable<MailboxConversation> {
    const params = new HttpParams()
      .set('folder', folder)
      .set('uidValidity', uidValidity);

    return this.http
      .get<MailboxConversation>(`${this.baseUrl(mailAccountId)}/messages/${uid}/conversation`, this.options(params))
      .pipe(
        timeout(MailboxService.TIMEOUT_MS),
        map(conversation => ({
          ...conversation,
          messages: (conversation.messages ?? []).map(message => ({ ...message, date: new Date(message.date) }))
        })),
        catchError(this.toMailboxError)
      );
  }

  getMessage(
    mailAccountId: string,
    folder: string,
    uid: number,
    uidValidity: number,
    allowRemoteImages: boolean = false
  ): Observable<MailboxMessage> {
    const params = new HttpParams()
      .set('folder', folder)
      .set('uidValidity', uidValidity)
      .set('allowRemoteImages', allowRemoteImages);

    return this.http
      .get<MailboxMessage>(`${this.baseUrl(mailAccountId)}/messages/${uid}`, this.options(params))
      .pipe(
        timeout(MailboxService.TIMEOUT_MS),
        map(message => ({ ...message, date: new Date(message.date) })),
        catchError(this.toMailboxError)
      );
  }

  /// URL del documento del cuerpo, que carga el iframe aislado.
  ///
  /// Va autenticada por la cookie `Authorization` que planta el inicio de sesión, porque un iframe no puede
  /// enviar cabeceras. Es el mismo mecanismo con el que ya funcionan las descargas de ficheros.
  getBodyUrl(mailAccountId: string, folder: string, uid: number, uidValidity: number, allowRemoteImages: boolean): string {
    const query = new URLSearchParams({
      folder,
      uidValidity: String(uidValidity),
      allowRemoteImages: String(allowRemoteImages)
    });
    return `${this.baseUrl(mailAccountId)}/messages/${uid}/body?${query.toString()}`;
  }

  /// URL de descarga de un adjunto. Autenticada por cookie, igual que el cuerpo.
  getPartUrl(mailAccountId: string, folder: string, uid: number, uidValidity: number, partId: string): string {
    const query = new URLSearchParams({ folder, uidValidity: String(uidValidity) });
    return `${this.baseUrl(mailAccountId)}/messages/${uid}/parts/${partId}?${query.toString()}`;
  }

  getSourceUrl(mailAccountId: string, folder: string, uid: number, uidValidity: number): string {
    const query = new URLSearchParams({ folder, uidValidity: String(uidValidity) });
    return `${this.baseUrl(mailAccountId)}/messages/${uid}/source?${query.toString()}`;
  }

  // --------------------------------------------------------------- redacción y envío

  /// Borrador para responder, responder a todos o reenviar. La cita del original la monta el servidor, que es quien
  /// tiene el mensaje y quien sabe sanearlo.
  getDraft(
    mailAccountId: string,
    folder: string,
    uid: number,
    uidValidity: number,
    mode: MailboxComposeMode
  ): Observable<MailboxDraft> {
    const params = new HttpParams()
      .set('folder', folder)
      .set('uidValidity', uidValidity)
      .set('mode', mode);

    return this.http
      .get<MailboxDraft>(`${this.baseUrl(mailAccountId)}/messages/${uid}/draft`, this.options(params))
      .pipe(timeout(MailboxService.TIMEOUT_MS), catchError(this.toMailboxError));
  }

  /// Envía. El plazo es más holgado que el resto: hay que conectar por SMTP, subir los adjuntos y guardar la copia.
  send(mailAccountId: string, request: MailboxSendRequest): Observable<MailboxSendResult> {
    return this.http
      .post<MailboxSendResult>(`${this.baseUrl(mailAccountId)}/send`, request, this.options())
      .pipe(timeout(MailboxService.SEND_TIMEOUT_MS), catchError(this.toMailboxError));
  }

  // --------------------------------------------------------------- modificación

  setFlags(
    mailAccountId: string,
    folder: string,
    uidValidity: number,
    uids: number[],
    flags: { seen?: boolean; flagged?: boolean; answered?: boolean }
  ): Observable<number> {
    return this.http
      .post<{ affected: number }>(
        `${this.baseUrl(mailAccountId)}/messages/flags`,
        { folder, uidValidity, uids, ...flags },
        this.options()
      )
      .pipe(timeout(MailboxService.TIMEOUT_MS), map(result => result.affected), catchError(this.toMailboxError));
  }

  move(mailAccountId: string, folder: string, uidValidity: number, uids: number[], targetFolder: string): Observable<number> {
    return this.http
      .post<{ affected: number }>(
        `${this.baseUrl(mailAccountId)}/messages/move`,
        { folder, uidValidity, uids, targetFolder },
        this.options()
      )
      .pipe(timeout(MailboxService.TIMEOUT_MS), map(result => result.affected), catchError(this.toMailboxError));
  }

  delete(mailAccountId: string, folder: string, uidValidity: number, uids: number[]): Observable<MailboxDeleteResult> {
    return this.http
      .post<MailboxDeleteResult>(
        `${this.baseUrl(mailAccountId)}/messages/delete`,
        { folder, uidValidity, uids },
        this.options()
      )
      .pipe(timeout(MailboxService.TIMEOUT_MS), catchError(this.toMailboxError));
  }

  // --------------------------------------------------------------- errores

  /// Traduce el fallo a un `MailboxError` clasificado. Único sitio donde se decide qué significa cada respuesta.
  private toMailboxError = (error: unknown) => {
    if (error instanceof HttpErrorResponse) {
      const body: any = error.error ?? {};
      const detail: string = body.detail ?? '';
      const retryable: boolean = body.retryable ?? false;

      const kind = MailboxService.kindFromResponse(error.status, body.code);
      return throwError(() => new MailboxError(kind, detail || MailboxService.defaultMessage(kind), retryable, error.status));
    }

    //rxjs lanza TimeoutError cuando se agota el plazo del cliente
    if ((error as any)?.name === 'TimeoutError') {
      return throwError(() => new MailboxError('timeout', MailboxService.defaultMessage('timeout'), true, 0));
    }

    return throwError(() => new MailboxError('unknown', MailboxService.defaultMessage('unknown'), true, 0));
  };

  private static kindFromResponse(status: number, code?: string): MailboxErrorKind {
    switch (code) {
      case 'mailbox.auth': return 'auth';
      case 'mailbox.connection': return 'connection';
      case 'mailbox.command_rejected': return 'commandRejected';
      case 'mailbox.timeout': return 'timeout';
      case 'mailbox.not_found': return 'notFound';
      case 'mailbox.stale_folder': return 'staleFolder';
      case 'mailbox.busy': return 'busy';
      case 'mailbox.forbidden': return 'forbidden';
      case 'mailbox.account_not_found': return 'accountNotFound';
    }

    //sin código reconocible se decide por el estado. El 0 es "no se pudo ni conectar con la API".
    if (status === 0) return 'connection';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'accountNotFound';
    if (status === 409) return 'staleFolder';
    if (status === 410) return 'notFound';
    if (status === 499) return 'cancelled';
    if (status === 503) return 'busy';
    if (status === 504) return 'timeout';
    if (status === 502) return 'connection';
    return 'unknown';
  }

  private static defaultMessage(kind: MailboxErrorKind): string {
    switch (kind) {
      case 'connection': return 'No se ha podido conectar con el servidor de correo.';
      case 'commandRejected': return 'El servidor de correo ha rechazado la operación.';
      case 'timeout': return 'El servidor de correo ha tardado demasiado en responder.';
      case 'auth': return 'El servidor de correo ha rechazado las credenciales de esta cuenta.';
      case 'forbidden': return 'No tienes permiso para esta operación sobre el buzón.';
      case 'notFound': return 'El mensaje ya no está en esta carpeta.';
      case 'staleFolder': return 'La carpeta ha cambiado en el servidor. Vuelve a cargarla.';
      case 'busy': return 'El buzón está ocupado ahora mismo. Inténtalo de nuevo en unos segundos.';
      case 'accountNotFound': return 'La cuenta de correo no existe o no tiene el buzón habilitado.';
      case 'cancelled': return 'Operación cancelada.';
      default: return 'Error inesperado al consultar el buzón.';
    }
  }
}
